import * as React from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { cn } from '@/lib/utils';
import { UploadCloud, X } from 'lucide-react';
import { Input } from '@/components/atoms';

export interface FileUploadProps {
  value?: File | null;
  onChange?: (file: File | null) => void;
  label?: string;
  recommended?: string;
  maxSizeMb?: number;
  className?: string;
  existingUrl?: string;
}

export function FileUpload({
  value,
  onChange,
  label = 'Drag or drop image',
  recommended = 'Recommended dimension: 930px x 1163px',
  maxSizeMb = 2,
  className,
  existingUrl,
}: FileUploadProps) {
  const [error, setError] = React.useState<string | null>(null);
  const [localFile, setLocalFile] = React.useState<File | null>(null);

  const onDrop = React.useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        setError(`Max file size: ${maxSizeMb}mb`);
        setLocalFile(null);
        onChange?.(null);
        return;
      }
      const file = acceptedFiles[0];
      setError(null);
      setLocalFile(file);
      onChange?.(file);
    },
    [maxSizeMb, onChange]
  );

  React.useEffect(() => {
    if (value) setLocalFile(value);
    if (!value) setLocalFile(null);
  }, [value]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    maxSize: maxSizeMb * 1024 * 1024,
    multiple: false,
  });

  return (
    <div
      {...getRootProps({
        className: cn(
          'border-2 border-dashed rounded-md bg-accent/30 flex flex-col items-center justify-center w-full px-4 py-8 cursor-pointer transition-colors',
          isDragActive ? 'border-primary bg-primary/50' : 'border-input',
          className
        ),
        tabIndex: 0,
        role: 'button',
        'aria-label': 'Upload file',
      })}
    >
      <Input {...getInputProps()} />
      <div className="flex flex-col items-center gap-2">
        <UploadCloud />
        <span className="text-gray-500 text-sm font-medium">{label}</span>
        <span className="text-gray-400 text-xs text-center">{recommended}</span>
        <span className="text-gray-400 text-xs">
          Max file size: {maxSizeMb}mb
        </span>
      </div>
      {(localFile || existingUrl) && (
        <div className="mt-4 flex flex-col items-center gap-2">
          <div className="relative">
            <img
              src={localFile ? URL.createObjectURL(localFile) : existingUrl}
              alt="Preview"
              className="max-h-40 rounded-md border"
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLocalFile(null);
                onChange?.(null);
              }}
              className="absolute -top-2 -right-2 rounded-full bg-black/70 text-white p-1 hover:bg-black"
              aria-label="Remove image"
            >
              <X className="size-3" />
            </button>
          </div>
          {localFile && (
            <span className="text-xs text-gray-500">{localFile.name}</span>
          )}
        </div>
      )}
      {error && <span className="text-destructive text-xs mt-2">{error}</span>}
    </div>
  );
}
